import { forwardRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Input, cn } from '@kidshome/ui';

/**
 * Password field (FSD app `shared/ui`) — the shared styled {@link Input} plus a trailing eye/eye-off
 * toggle that flips the input `type` between `password` and `text`. The design system (`packages/ui`)
 * is intentionally i18n-free, so the localized toggle label lives here in the app layer.
 *
 * A11y: the toggle is a real `<button type="button">` (never submits the surrounding form), carries a
 * localized `aria-label` ("Show password" / "Hide password") and `aria-pressed` reflecting the
 * revealed state, and keeps focus on itself after a click. The value is never logged.
 *
 * Drop-in for the bare `<Input type="password" />`: it forwards the `ref` and spreads every native
 * input prop, so react-hook-form's `register(...)` (ref + name + onChange + onBlur) wires up exactly
 * as before. Pass the usual `id`, `autoComplete`, `aria-invalid`, `aria-describedby`, etc.
 */
export const PasswordInput = forwardRef<
  HTMLInputElement,
  Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type'>
>(({ className, ...props }, ref) => {
  const { t } = useTranslation('common');
  const [revealed, setRevealed] = useState(false);

  return (
    <div className="relative">
      <Input
        ref={ref}
        type={revealed ? 'text' : 'password'}
        // Leave room for the toggle button (logical padding-inline-end → RTL-safe).
        className={cn('pe-10', className)}
        {...props}
      />
      <button
        type="button"
        // Reveal toggle: aria-pressed reflects "is the password currently visible"; the label always
        // names the ACTION the press performs, so SRs announce "Show password" when hidden.
        aria-pressed={revealed}
        aria-label={revealed ? t('password.hide') : t('password.show')}
        onClick={() => setRevealed((v) => !v)}
        className="absolute inset-y-0 end-0 flex w-10 items-center justify-center rounded-e-md text-text-muted outline-none transition-colors hover:text-text focus-visible:ring-2 focus-visible:ring-ring"
      >
        {revealed ? <EyeOffIcon /> : <EyeIcon />}
      </button>
    </div>
  );
});
PasswordInput.displayName = 'PasswordInput';

function EyeIcon(): React.ReactElement {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

function EyeOffIcon(): React.ReactElement {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c6.5 0 10 7 10 7a13.16 13.16 0 0 1-1.67 2.68" />
      <path d="M6.61 6.61A13.526 13.526 0 0 0 2 12s3.5 7 10 7a9.74 9.74 0 0 0 5.39-1.61" />
      <path d="M14.12 14.12a3 3 0 1 1-4.24-4.24" />
      <line x1="2" y1="2" x2="22" y2="22" />
    </svg>
  );
}
